import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';

const MINUTES_PER_QUARTER_HOUR = 15;
const LAST_QUARTER_HOUR_ALLOWANCE = (60 - MINUTES_PER_QUARTER_HOUR) / 60.0;
// Two hour digits' worth, the ceiling every field had while the hours part was truncated to its
// display width. That width is arbitrary rather than a real period, so the whole of its last hour
// stays usable: 99:45. Fields whose maximum IS a period pass that exact value instead.
const DEFAULT_MAX_HOURS = 99 + LAST_QUARTER_HOUR_ALLOWANCE;

/**
 * Rounds the minutes up to the next quarter hour, carrying a full hour into the hours part.
 *
 * @param {number} hours
 * @param {number} minutes
 * @returns {number} hours, as a multiple of a quarter
 */
const toQuarterHours = (hours, minutes) => {
    const quantisedMinutes = Math.ceil(minutes / MINUTES_PER_QUARTER_HOUR) * MINUTES_PER_QUARTER_HOUR;
    return hours + quantisedMinutes / 60.0;
};

/**
 * Reads "8.5" / "8,5" as fractional hours. Comma is accepted because fr is a live locale.
 *
 * @param {string} stringValue
 * @returns {number}
 */
const parseDecimalHours = (stringValue) => {
    const decimalHours = Number(stringValue.replace(/[^\d.,]/g, '').replace(',', '.'));
    if (Number.isNaN(decimalHours)) {
        return 0;
    }
    const wholeHours = Math.floor(decimalHours);
    return toQuarterHours(wholeHours, (decimalHours - wholeHours) * 60);
};

/**
 * @param {string} stringValue
 * @returns {number}
 */
const parseClockHours = (stringValue) => {
    const digitsAndColon = stringValue.replace(/[^\d:]/g, '');
    const split = digitsAndColon.includes(':') ? digitsAndColon.split(':') : `${digitsAndColon}:`.split(':');
    return toQuarterHours(Number(split[0]), Number(split[1].substring(0, 2)));
};

/**
 * @param {string} stringValue "8:30", or "8.5" / "8,5" for the same duration
 * @param {number} [maxHours] the exact maximum; anything above it is clamped down to it
 * @returns {number}
 */
const parseTimeString = (stringValue, maxHours = DEFAULT_MAX_HOURS) => {
    const hours = /[.,]/.test(stringValue) && !stringValue.includes(':')
        ? parseDecimalHours(stringValue)
        : parseClockHours(stringValue);
    // Clamped after the carry, so 24:59 lands on the maximum instead of stepping past it.
    return Math.min(hours, maxHours);
};

/**
 * @param {number} num
 */
const setTwoDigits = (num) => `0${Math.floor(num)}`.slice(-2);

// Pads to at least minDigits and never truncates, so 120 hours renders as "120", not "20".
const padDigits = (num, minDigits) => `${Math.floor(num)}`.padStart(minDigits, '0');

/**
 * @param {number} value
 * @param {number} [minHoursDigits]
 * @returns {string}
 */
const getTimeString = (value, minHoursDigits = 2) => {
    const hours = padDigits(value, minHoursDigits);
    const minutes = setTwoDigits((value * 60) % 60);
    return `${hours}:${minutes}`;
};

const DurationInput = ({ onChange, value, minHoursDigits = 2, maxHours = DEFAULT_MAX_HOURS, ...others }) => {
    const [displayedValue, setDisplayedValue] = useState(others.placeholder && (value === null || value === 0) ? "" : getTimeString(value, minHoursDigits));
    const changeOnBlur = (ev, updatedValue) => {
        const parsedValue = parseTimeString(ev.target.value, maxHours);
        onChange(parsedValue, updatedValue);
        setDisplayedValue(getTimeString(parsedValue, minHoursDigits));
    };

    useEffect(() => {
        setDisplayedValue(others.placeholder && (value === null || value === 0) ? "" : getTimeString(value, minHoursDigits));
    }, [value]);

    return (
        <TextField
            size={others.size ?? "medium"}
            onChange={(ev) => setDisplayedValue(ev.target.value)}
            onBlur={changeOnBlur}
            value={displayedValue}
            {...others}
        />
    );
};

export { parseTimeString, getTimeString };

export default DurationInput;
